import {Comment} from '../../types/comment';
import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {NoteService} from '../../services/note_service';
import {UserService} from '../../services/user_service';
import {ModalDismissReasons, NgbModal, NgbModalOptions} from '@ng-bootstrap/ng-bootstrap';
import {ReportCommentModalComponent} from '../report-comment-modal/report-comment-modal.component';

import * as Debug from 'debug';
import { User } from '@sentry/types/types/user';
import { Note } from '../../types/note';
import { ConfirmationModalComponent } from '../../confirmation-modal/confirmation-modal.component';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

const debug = Debug('notd:comment');
export class BlockedUsersList {
  blockedUsers: any[];
  users: any[];
}

@Component({
  selector: 'app-comment',
  templateUrl: './comment.component.html',
  styleUrls: ['./comment.component.scss']
})
export class CommentComponent implements OnInit {
  @ViewChild('blockUserModal', {static: false}) blockUserModal: ElementRef;

  @Input()
  public comment: Comment;

  @Input()
  public newReply: string;

  @Input()
  public level: string;

  @Output() refreshComments: EventEmitter<any> = new EventEmitter();

  public replyCollapsed = false;

  editMode = false;
  editedContent = '';
  public noteOwnerId: string | number;
  public blockedUsersList: BlockedUsersList;
  public isUserCommentsAdmin: boolean;
  public isCommentFromCurrentUser: boolean;
  public isCommentUserBlocked = false;
  public blockUserForm: UntypedFormGroup;

  private streamId: string | number;

  constructor(
    private modalService: NgbModal,
    private noteService: NoteService,
    private fb: UntypedFormBuilder,
    private userService: UserService) { }

  public ngOnInit(): void {
    this.streamId = this.comment.channelId;

    if (this.comment.comments) {
      this.comment.comments = this.removeNewlineFromComment(this.comment.comments);
    }

    if (this.comment.postId) {
      this.noteService.getNote(this.comment.postId).then((note: Note) => {
        this.noteOwnerId = note.userId;
        this.isUserCommentsAdmin = this.userService.curUser && this.userService.curUser.id.toString() === this.noteOwnerId.toString();

        if (this.isUserCommentsAdmin) {
          this.getBlockedUsersList();
        }
      });
    }

    this.isCommentFromCurrentUser = this.userService.curUser && this.comment.user && this.userService.curUser.id === this.comment.user.id;
    this.createBlockUserForm();
  }

  addReply() {
    this.noteService.commentOnNote(this.comment.postId, this.newReply, this.comment.id).then((comment) => {
      this.newReply = '';
      this.replyCollapsed = false;
      this.refreshComments.emit(true);
    });
  }

  public blockCommentOwner(): void {
    this.blockUserForm.reset();
    const modalRef = this.modalService.open(this.blockUserModal, { windowClass: 'block-user-modal', size: 'lg' });
  }

  public confirmUserBlocking(): void {
    const comment = this.blockUserForm.get('comment').value;
    this.userService.blockCommentUser(this.streamId, this.comment.userId, comment).then(res => {
      this.refreshComments.emit(true);
    });
  }

  likeComment() {
    this.noteService.likeCommentOnNote(this.comment.postId, this.comment.id)
      .then((resp) => {
        this.refreshComments.emit(true);
      }, err => {
        debug('error:', err);
      }).catch(err => {
        debug('Caught multiple like.', err);
      });
  }

  dislikeComment() {
    this.noteService.dislikeCommentOnNote(this.comment.postId, this.comment.id)
      .then((resp) => {
        this.refreshComments.emit(true);
      }, err => {
        debug('error:', err);
      }).catch(err => {
        debug('Caught multiple dislike.', err);
      });
  }

  deleteComment() {
    this.noteService.deleteComment(this.comment.postId, this.comment.id).subscribe(res => {
      this.refreshComments.emit(true);
    });
  }

  public deleteUserComment(): void {
    const modalOptions: NgbModalOptions = {};
    const refModalConf = this.modalService.open(ConfirmationModalComponent, modalOptions);

    refModalConf.componentInstance.confirmationModalTitle = 'Delete user comment';
    refModalConf.componentInstance.confirmationModalMsg = 'Are you sure you want to delete this comment?';

    refModalConf.result.then(resp => {
        if (resp) {
          this.noteService.deleteComment(this.comment.postId, this.comment.id).subscribe(() => {
            this.refreshComments.emit(true);
          });
        } else {
          return false;
        }
    })
    .catch(err => {
      console.log(err);
    });
  }

  editComment() {
    this.editMode = true;
    this.editedContent = this.comment.comments;
  }

  saveEditedComment() {
    this.noteService.updateComment(this.comment.postId, this.comment.id, this.editedContent).subscribe(res => {
      this.stopEditing();
      this.refreshComments.emit(true);
    });
  }

  stopEditing() {
    this.editMode = false;
    this.editedContent = '';
  }

  getNextLevel() {
    // eslint-disable-next-line radix
    return Number.parseInt(this.level, 0) + 1;
  }

  public openReportCommentModal(postId: any, commentId: any) {
    const modalRef = this.modalService.open(ReportCommentModalComponent);
    modalRef.result.then((result) => {
      debug(`Report Closed with:`, result);
    }, (reason) => {
      debug(`Dismissed ${this.getDismissReason(reason)}`);
    });
    modalRef.componentInstance.postId = postId;
    modalRef.componentInstance.commentId = commentId;
  }

  private getBlockedUsersList(): void {
    this.userService.getBlockedUsers(this.streamId).then((blockedList: BlockedUsersList | null) => {
      for (const u of blockedList.blockedUsers) {
        if (u.userId === this.comment.user.id) {
          this.isCommentUserBlocked = true;
        }
      }
    });
  }

  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }

  private createBlockUserForm(): void {
    this.blockUserForm = this.fb.group({
        comment: ['', [
          Validators.required]
        ]
    });
  }

  private removeNewlineFromComment(text: string): string {
    return `"${text}"`
      .replace(/'/g, '`')
      .replace(/^"|"$/g, '')
      .replace(/<br\s*\/?>/gi, '\n');
  }
}
